import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import {
  Eye, ShieldCheck, Compass, CheckCircle2, ArrowRight, Anchor, AlertCircle,
} from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES } from "@/data/content";

const ICONS = { Eye, ShieldCheck, Compass };

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" replace />;

  const Icon = ICONS[service.icon] || Compass;
  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`service-page-${slug}`}>
      <Seo
        title={`${service.name} — Hussar Marine Concierge`}
        description={service.summary}
      />
      <PageHero eyebrow="Service" title={service.name} subtitle={service.tagline} image={service.hero} />

      <section className="py-20 lg:py-28 bg-white dark:bg-navy-900">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <Reveal>
              <div className="h-14 w-14 rounded-2xl bg-navy-900 dark:bg-gold-500/10 flex items-center justify-center">
                <Icon className="h-7 w-7 text-gold-500" />
              </div>
              <h2 className="mt-6 text-3xl font-serif font-bold text-navy-900 dark:text-white">
                What {service.name} covers
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                {service.summary}
              </p>
            </Reveal>
            <Reveal delay={120}>
              <ul className="mt-8 space-y-4">
                {service.includes.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-gold-500 mt-0.5 shrink-0" />
                    <span className="text-slate-700 dark:text-slate-200">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-8 flex items-start gap-3 rounded-xl border border-gold-500/30 bg-gold-100/60 dark:bg-navy-800 p-5">
                <AlertCircle className="h-5 w-5 text-gold-600 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700 dark:text-slate-300">{service.note}</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-2">
            <Reveal delay={150}>
              <div className="sticky top-28 rounded-2xl border border-gold-500/20 bg-ivory dark:bg-navy-800 p-8">
                <h3 className="font-serif text-xl text-navy-900 dark:text-white">Request this service</h3>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                  Share your vessel details and marina. We'll confirm scope and scheduling
                  before anything begins.
                </p>
                <Link
                  to="/request-inspection"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold px-6 py-3.5 rounded-full transition-colors"
                  data-testid="service-request-button"
                >
                  <Anchor className="h-4 w-4" /> Request an Inspection
                </Link>
                <Link
                  to="/pricing"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-navy-900/15 dark:border-white/15 text-navy-900 dark:text-white font-semibold px-6 py-3.5 rounded-full hover:border-gold-500 transition-colors"
                >
                  View Plans <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-16 bg-ivory dark:bg-navy-800">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <p className="eyebrow text-gold-600 dark:text-gold-400 mb-6">Other Services</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {others.map((s) => {
              const OtherIcon = ICONS[s.icon] || Compass;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="card-lift flex items-center gap-4 rounded-2xl border border-gold-500/20 bg-white dark:bg-navy-900 p-6"
                  data-testid={`related-service-${s.slug}`}
                >
                  <div className="h-12 w-12 rounded-xl bg-navy-900 dark:bg-gold-500/10 flex items-center justify-center shrink-0">
                    <OtherIcon className="h-6 w-6 text-gold-500" />
                  </div>
                  <div>
                    <p className="font-serif text-lg text-navy-900 dark:text-white">{s.name}</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{s.tagline}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gold-500 ml-auto" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
